"use client";

import { heightNavigatonPx, sectionAnchorScrollMarginPx } from "@/lib/consts";
import {
  Box,
  Container,
  Stack,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { SLContainerStatic } from "./SLContainerStatic";
import { SLHtmlFromLangFile } from "../sections/home/SLHtmlFromLangFile";

type SLContainerProps = {
  id?: string;
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  children?: React.ReactNode;
  ref?: React.RefObject<HTMLDivElement | null>;
  isLeftSideSticky?: boolean;
  isTwoColumns?: boolean;
  hasPaddingTop?: boolean;
  width?: "xl" | "lg" | "md" | "sm" | "xs";
  sx?: SxProps;
};

function SLContainerMotion({
  id,
  title,
  subtitle,
  children,
  ref,
  isLeftSideSticky,
  isTwoColumns,
  hasPaddingTop,
  width,
  sx,
}: SLContainerProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);
  const y = useTransform(scrollYProgress, [0, 0.1], [0, 0]);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const verticalPadding = isMobile ? 5 : 10;

  return (
    <Container
      component="section"
      maxWidth={width}
      id={id}
      ref={ref}
      sx={{
        overflow: "clip",
        pb: verticalPadding,
        position: "relative",
        ...(!isMobile && isLeftSideSticky
          ? { width: "100%", minHeight: "98vh", height: "fit-content" }
          : { width: "100%", height: "fit-content" }),
        ...(id ? { scrollMarginTop: `${sectionAnchorScrollMarginPx}px` } : {}),
        ...sx,
      }}
    >
      <Stack
        ref={containerRef}
        direction={
          !isMobile && (isLeftSideSticky || isTwoColumns) ? "row" : "column"
        }
        pt={hasPaddingTop ? verticalPadding : 0}
        sx={{
          position: "relative",
          height: "fit-content",
        }}
      >
        <Stack
          pr={isMobile ? 0 : 10}
          sx={{
            ...(isTwoColumns && !isMobile
              ? { width: "50%", maxWidth: "50%", minWidth: "50%" }
              : !isMobile && isLeftSideSticky
                ? {
                    position: "sticky",
                    top: `calc(${heightNavigatonPx}px + 15vh)`,
                    height: "100%",
                    width: "50%",
                    maxWidth: "50%",
                    minWidth: "50%",
                  }
                : { width: "100%" }),
          }}
        >
          <Box
            component={motion.div}
            style={{
              opacity: !isMobile && isLeftSideSticky ? opacity : 1,
              y: !isMobile && isLeftSideSticky ? y : 0,
            }}
            sx={{
              width: "100%",
            }}
          >
            <Stack>
              {title &&
                (typeof title === "string" ? (
                  <Typography variant="h2" component="h2" mb={subtitle ? 3 : 5}>
                    <SLHtmlFromLangFile htmlString={title} />
                  </Typography>
                ) : (
                  title
                ))}
              {subtitle && typeof subtitle === "string" ? (
                <Typography
                  variant="body1"
                  fontSize={theme.typography.h6.fontSize}
                  mb={title ? 10 : 0}
                  mr={!isMobile && isLeftSideSticky ? 10 : 0}
                >
                  <SLHtmlFromLangFile htmlString={subtitle} />
                </Typography>
              ) : (
                subtitle
              )}
            </Stack>
          </Box>
        </Stack>
        <Stack
          sx={{
            height: "fit-content",
            position: "relative",
            ...(!isMobile && isLeftSideSticky
              ? { width: "50%", maxWidth: "50%", minWidth: "50%" }
              : { width: "100%" }),
          }}
        >
          {children}
        </Stack>
      </Stack>
    </Container>
  );
}

export const SLContainer = (props: SLContainerProps) => {
  if (!props.isLeftSideSticky && !props.isTwoColumns) {
    return <SLContainerStatic {...props} />;
  }

  return <SLContainerMotion {...props} />;
};
